import React, { useState, useEffect, useRef } from "react";
import { BsChatDotsFill } from "react-icons/bs";
import { AiOutlineClose, AiOutlineSend } from "react-icons/ai";
import { apiConnector } from "../../services/apiconnector";
import { courseEndpoints } from "../../services/apis";

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      sender: "bot",
      text: "Hi there! I am your AI assistant powered by Gemini. How can I help you today? Please briefly describe what you need help with.",
    },
  ]);
  const [inputText, setInputText] = useState("");
  const [courses, setCourses] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  // Fetch courses implicitly for later search
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const res = await apiConnector("GET", courseEndpoints.GET_ALL_COURSE_API);
        if (res?.data?.success) {
          setCourses(res.data.data);
        }
      } catch (error) {
        console.error("Failed to fetch courses for chatbot", error);
      }
    };
    fetchCourses();
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isOpen, isTyping]);

  const callGeminiAPI = async (userText) => {
    try {
      const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
      if (!apiKey) {
        return "Sorry, I am missing my API key configuration! Ensure REACT_APP_GEMINI_API_KEY is defined in .env";
      }

      const availableCourses = courses.map(c => `Name: ${c.courseName}, Price: ${c.price}, Description: ${c.courseDescription || "N/A"}`).join(" | ");
      
      const systemPrompt = `You are StudyNotion AI, a friendly, highly intelligent support assistant for an online coding academy called StudyNotion. 
Your goal is to help the user with their problems. If they want to know about courses, here is the live database of our courses: [${availableCourses}].
Always answer politely and concisely. If they have a complicated issue, ask them to provide their name, email, and mobile number so a human can reach out. Be super conversational and helpful! Give the responses in simple text.`;

      // Build chat log manually to prevent strict role errors
      let chatLog = messages.map(m => `${m.sender === "bot" ? "StudyNotion AI" : "User"}: ${m.text}`).join("\n");
      const finalPrompt = `${systemPrompt}\n\nChat History:\n${chatLog}\nUser: ${userText}\nStudyNotion AI:`;

      const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-flash-latest:generateContent?key=${apiKey}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contents: [{ parts: [{ text: finalPrompt }] }]
        })
      });

      const data = await response.json();
      if (data.candidates && data.candidates.length > 0) {
        return data.candidates[0].content.parts[0].text;
      } else {
        console.error("Gemini Error:", data);
        return "I apologize, but I am currently having trouble thinking of a response.";
      }
    } catch (error) {
      console.error(error);
      return "I apologize, but my servers are currently offline.";
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputText.trim()) return;

    const userMessage = {
      id: Date.now(),
      sender: "user",
      text: inputText,
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputText("");
    setIsTyping(true);

    const botResponseText = await callGeminiAPI(userMessage.text);

    setMessages((prev) => [
      ...prev,
      {
        id: Date.now(),
        sender: "bot",
        text: botResponseText,
      },
    ]);
    setIsTyping(false);
  };

  return (
    <div className="fixed bottom-6 right-6 z-[1000]">
      {/* Chat Window */}
      {isOpen && (
        <div className="mb-4 flex h-[480px] w-[350px] flex-col overflow-hidden rounded-2xl border border-richblack-700 bg-richblack-800 shadow-xl transition-all duration-300 sm:w-[400px]">
          {/* Header */}
          <div className="flex items-center justify-between bg-richblack-900 px-4 py-4 text-richblack-5 shadow-sm">
            <div className="flex items-center gap-x-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-yellow-50">
                <BsChatDotsFill className="text-xl text-richblack-900" />
              </div>
              <div>
                <h3 className="font-semibold text-richblack-5">StudyNotion AI</h3>
                <p className="text-xs text-richblack-300">Powered by Gemini AI</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-richblack-200 transition-colors hover:text-white"
            >
              <AiOutlineClose className="text-xl" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 custom-scrollbar">
            <div className="flex flex-col gap-y-4">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${
                    msg.sender === "user" ? "justify-end" : "justify-start"
                  }`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm whitespace-pre-wrap ${
                      msg.sender === "user"
                        ? "bg-yellow-50 text-richblack-900 rounded-br-none"
                        : "bg-richblack-700 text-richblack-5 rounded-bl-none"
                    }`}
                  >
                    {msg.text}
                  </div>
                </div>
              ))}
              {isTyping && (
                <div className="flex justify-start">
                  <div className="max-w-[85%] rounded-2xl rounded-bl-none bg-richblack-700 px-4 py-3 text-sm text-richblack-200">
                    <span className="animate-pulse">Thinking...</span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Input Area */}
          <form
            onSubmit={handleSendMessage}
            className="flex items-center gap-x-2 border-t border-richblack-700 bg-richblack-800 p-4"
          >
            <input
              type="text"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder="Ask anything..."
              disabled={isTyping}
              className="flex-1 rounded-full border border-richblack-600 bg-richblack-700 px-4 py-2 text-sm text-richblack-5 focus:border-yellow-50 focus:outline-none disabled:opacity-50"
            />
            <button
              type="submit"
              disabled={!inputText.trim() || isTyping}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-yellow-50 text-richblack-900 transition-transform hover:scale-105 disabled:opacity-50"
            >
              <AiOutlineSend className="text-lg" />
            </button>
          </form>
        </div>
      )}

      {/* Floating Toggle Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="group relative flex h-16 w-16 items-center justify-center rounded-full bg-yellow-50 shadow-lg shadow-yellow-50/30 transition-all duration-300 hover:scale-110"
        >
          <BsChatDotsFill className="text-3xl text-richblack-900" />
          <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-caribbeangreen-200 text-[10px] font-bold text-richblack-900 animate-bounce">
            1
          </span>
        </button>
      )}
    </div>
  );
};

export default Chatbot;
